// src/components/AuthScreen.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { validateEmail } from '../utils/helpers';
import supabaseService from '../services/supabaseService';
import { FiEye, FiEyeOff, FiMail, FiLock, FiUser, FiDownload, FiX, FiSmartphone } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const OFFLINE_USER_KEY = 'khidma_cached_user';
const saveUserToCache = (user) => {
  try { localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(user)); } catch (_) {}
};

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.fares.khidma';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// ── نافذة تعليمات التثبيت ──────────────────────────────
const InstallGuide = ({ onClose }) => {
  const isIOS     = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isMobile  = isIOS || isAndroid;

  const steps = isIOS ? [
    { icon: '1', text: 'اضغط زر المشاركة', sub: 'الزر الموجود أسفل الشاشة ⬆️' },
    { icon: '2', text: 'اختر "Add to Home Screen"', sub: 'مرر للأسفل في القائمة' },
    { icon: '3', text: 'اضغط "إضافة"', sub: 'سيظهر التطبيق على شاشتك الرئيسية' },
  ] : isAndroid ? [
    { icon: '1', text: 'اضغط النقاط الثلاث ⋮', sub: 'أعلى يمين متصفح Chrome' },
    { icon: '2', text: 'اختر "إضافة إلى الشاشة الرئيسية"', sub: 'أو "تثبيت التطبيق"' },
    { icon: '3', text: 'اضغط "إضافة"', sub: 'سيظهر التطبيق على شاشتك الرئيسية' },
  ] : [
    { icon: '1', text: 'اضغط النقاط الثلاث ⋮', sub: 'أعلى يمين متصفح Chrome أو Edge' },
    { icon: '2', text: 'اختر "البث والحفظ والمشاركة"', sub: 'في القائمة المنسدلة' },
    { icon: '3', text: 'اضغط "تثبيت الصفحة كتطبيق"', sub: 'ثم اضغط "تثبيت" في النافذة' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-[slideUp_0.3s_ease]"
        style={{ animation: 'slideUp 0.3s cubic-bezier(0.16,1,0.3,1)' }}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">تثبيت التطبيق</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isIOS ? 'iPhone / iPad' : isAndroid ? 'Android' : 'الحاسوب'}
            </p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
            <FiX className="text-gray-600 text-sm" />
          </button>
        </div>

        {/* Steps */}
        <div className="p-5 space-y-4">
          {steps.map((step, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {step.icon}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{step.text}</p>
                <p className="text-xs text-gray-500 mt-0.5">{step.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Visual hint for desktop */}
        {!isMobile && (
          <div className="mx-5 mb-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
              {/* Fake browser bar */}
              <div className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 truncate">
                khidma4u.netlify.app
              </div>
              {/* Highlight the 3 dots */}
              <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center font-bold text-base leading-none animate-pulse">
                ⋮
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mt-2 text-center">
              اضغط على النقاط الثلاث في متصفحك ↑
            </p>
          </div>
        )}

        <div className="px-5 pb-5">
          <button onClick={onClose}
            className="w-full bg-gray-900 text-white py-3 rounded-xl text-sm font-bold hover:bg-gray-800 transition-colors">
            فهمت ✓
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
//  MAIN
// ══════════════════════════════════════════════════════
const AuthScreen = () => {
  const [activeTab, setActiveTab]           = useState('login');
  const [loginData, setLoginData]           = useState({ email: '', password: '' });
  const [signupData, setSignupData]         = useState({ email: '', password: '' });
  const [showLoginPassword, setShowLoginPassword]   = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [loading, setLoading]               = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled]       = useState(false);
  const [showGuide, setShowGuide]           = useState(false);

  const { signIn, signUp, signInWithGoogle, loginAsGuest } = useAuth();
  const navigate = useNavigate();

  const handleGuestLogin = () => {
    loginAsGuest();
    navigate('/client');
  };

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowGuide(false);
      toast.success('تم تثبيت التطبيق بنجاح! 🎉');
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  useEffect(() => {
    // Disable body scrolling when AuthScreen is active to prevent scrollbars
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') toast.success('جاري التثبيت... 🎉');
      setDeferredPrompt(null);
      return;
    }
    setShowGuide(true);
  };

  const handleApkDownload = () => {
    window.open(PLAY_STORE_URL, '_blank');
    toast.success('جاري فتح متجر Google Play... 📱');
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      sessionStorage.setItem('google_oauth_initiated', 'true');
      await signInWithGoogle();
    } catch (error) {
      sessionStorage.removeItem('google_oauth_initiated');
      toast.error(error.message || 'فشل تسجيل الدخول بجوجل');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
  e.preventDefault();
  if (!validateEmail(loginData.email)) { toast.error('البريد الإلكتروني غير صحيح'); return; }
  if (loginData.password.length < 6)   { toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل'); return; }
  setLoading(true);
  try {
    const result = await signIn(loginData.email, loginData.password);
    if (result?.data?.user) saveUserToCache({ id: result.data.user.id, email: result.data.user.email });
    toast.success('تم تسجيل الدخول بنجاح');
    // لا حاجة للتنقل اليدوي هنا؛ App.jsx يتكفل بالتوجيه التلقائي
    // بمجرد أن يتحدد نوع المستخدم (زبون/مزود) في الـ context
  } catch (error) {
    toast.error(error.message || 'فشل تسجيل الدخول');
  } finally { setLoading(false); }
};

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateEmail(signupData.email)) { toast.error('البريد الإلكتروني غير صحيح'); return; }
    if (signupData.password.length < 6)   { toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل'); return; }
    setLoading(true);
    try {
      localStorage.setItem('verification_email', signupData.email);
      await signUp(signupData.email, signupData.password);
      toast.success('تم إرسال رمز التحقق إلى بريدك الإلكتروني', { duration: 3000 });
      navigate('/verify-email');
    } catch (error) {
      localStorage.removeItem('verification_email');
      toast.error(error.message || 'فشل إنشاء الحساب');
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="h-screen overflow-hidden bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-md flex flex-col justify-center">

          {/* Logo */}
          <div className="text-center mb-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-1">
              <FiUser className="text-3xl text-gray-900" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Khidma</h1>

            {/* Install Buttons Row */}
            <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
              {/* APK Download Button - always visible */}
              <button
                onClick={handleApkDownload}
                className="inline-flex items-center gap-1.5 bg-green-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-full hover:bg-green-700 active:scale-95 transition-all shadow-sm"
              >
                <FiSmartphone className="text-xs" />
                تحميل تطبيق Android
              </button>

              {/* PWA Install Button */}
              {!isInstalled && (
                <button
                  onClick={handleInstallClick}
                  className="inline-flex items-center gap-1.5 bg-gray-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-full hover:bg-gray-800 active:scale-95 transition-all shadow-sm"
                >
                  <FiDownload className="text-xs" />
                  تثبيت على سطح المكتب
                </button>
              )}
              {isInstalled && (
                <p className="text-[10px] text-green-600 font-medium">✓ التطبيق مثبت</p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center justify-center mb-4 space-x-6 space-x-reverse">
            <button onClick={() => setActiveTab('login')}
              className={`text-base font-medium transition-colors ${activeTab === 'login' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}>
              تسجيل الدخول
            </button>
            <div className="h-4 w-px bg-gray-200" />
            <button onClick={() => setActiveTab('signup')}
              className={`text-base font-medium transition-colors ${activeTab === 'signup' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}>
              إنشاء حساب
            </button>
          </div>

          {/* Forms */}
          <div className="bg-white">
            {activeTab === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-3">
                <button type="button" onClick={handleGuestLogin}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 rounded-lg font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
                  disabled={loading}>
                  <span>👀</span>
                  الدخول كضيف (تصفح فقط)
                </button>

                <button type="button" onClick={handleGoogleSignIn}
                  className="w-full bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={loading}>
                  <GoogleIcon />
                  تسجيل الدخول بجوجل
                </button>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"/></div>
                  <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-400">أو سجل دخولك</span></div>
                </div>

                <div className="relative group">
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <FiMail className="text-gray-400 group-focus-within:text-gray-900 transition-colors" />
                  </div>
                  <input type="email"
                    className="w-full py-2.5 pr-10 pl-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:bg-white transition-all text-gray-900 placeholder-gray-400"
                    placeholder="البريد الإلكتروني"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required disabled={loading}
                  />
                </div>

                <div className="relative group">
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <FiLock className="text-gray-400 group-focus-within:text-gray-900 transition-colors" />
                  </div>
                  <input type={showLoginPassword ? 'text' : 'password'}
                    className="w-full py-2.5 pr-10 pl-10 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:bg-white transition-all text-gray-900 placeholder-gray-400"
                    placeholder="كلمة المرور"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required disabled={loading}
                  />
                  <button type="button"
                    className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 hover:text-gray-900"
                    onClick={() => setShowLoginPassword(!showLoginPassword)} disabled={loading}>
                    {showLoginPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center">
                    <input id="remember-me" type="checkbox"
                      className="h-3.5 w-3.5 text-gray-900 border-gray-300 rounded cursor-pointer"
                      disabled={loading} />
                    <label htmlFor="remember-me" className="mr-1.5 text-xs text-gray-500 cursor-pointer">تذكرني</label>
                  </div>
                  <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-gray-900 transition-colors">
                    نسيت كلمة المرور؟
                  </Link>
                </div>

                <button type="submit"
                  className="w-full bg-gray-900 text-white py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}>
                  {loading
                    ? <div className="flex items-center justify-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"/>جاري تسجيل الدخول...</div>
                    : 'تسجيل الدخول'}
                </button>

              </form>
            ) : (
              <form onSubmit={handleSignup} className="space-y-3">
                <button type="button" onClick={handleGuestLogin}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 rounded-lg font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
                  disabled={loading}>
                  <span>👀</span>
                  الدخول كضيف (تصفح فقط)
                </button>

                <button type="button" onClick={handleGoogleSignIn}
                  className="w-full bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={loading}>
                  <GoogleIcon />
                  إنشاء حساب بجوجل
                </button>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"/></div>
                  <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-400">أو سجل ببريدك الإلكتروني</span></div>
                </div>

                <div className="relative group">
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <FiMail className="text-gray-400 group-focus-within:text-gray-900 transition-colors" />
                  </div>
                  <input type="email"
                    className="w-full py-2.5 pr-10 pl-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:bg-white transition-all text-gray-900 placeholder-gray-400"
                    placeholder="البريد الإلكتروني"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    required disabled={loading}
                  />
                </div>

                <div className="relative group">
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <FiLock className="text-gray-400 group-focus-within:text-gray-900 transition-colors" />
                  </div>
                  <input type={showSignupPassword ? 'text' : 'password'}
                    className="w-full py-2.5 pr-10 pl-10 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:bg-white transition-all text-gray-900 placeholder-gray-400"
                    placeholder="كلمة المرور (6 أحرف+)"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    required disabled={loading}
                  />
                  <button type="button"
                    className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 hover:text-gray-900"
                    onClick={() => setShowSignupPassword(!showSignupPassword)} disabled={loading}>
                    {showSignupPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>

                <button type="submit"
                  className="w-full bg-gray-900 text-white py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}>
                  {loading
                    ? <div className="flex items-center justify-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"/>جاري إنشاء الحساب...</div>
                    : 'إنشاء حساب'}
                </button>

                <p className="text-center text-[10px] text-gray-400 mt-1">
                  بإنشاء حساب توافق على{' '}
                  <Link to="/rules-and-terms" className="text-gray-600 font-medium hover:underline">الشروط والأحكام</Link>
                  {' '}و{' '}
                  <Link to="/privacy-policy" className="text-gray-600 font-medium hover:underline">سياسة الخصوصية</Link>
                </p>
              </form>
            )}
          </div>

        </div>
      </div>

      {/* Install Guide Modal */}
      {showGuide && <InstallGuide onClose={() => setShowGuide(false)} />}
    </>
  );
};

export default AuthScreen;